import { AiReviewSummary as AiReviewSummaryType } from '@sortlist-frontend/data-fetching/core-types';
import { Button, Chip, Tooltip, twMerge } from '@sortlist-frontend/design-system';
import { Lock, Sparkles } from '@sortlist-frontend/design-system/icons';
import { useIntercom } from '@sortlist-frontend/user-tools';
import { DeepNonNullable } from '@sortlist-frontend/utils';

import { useSharedComponentsContext } from '../utils';
import { AiReviewContent } from './AiReviewContent';
import { AiReviewSkeleton } from './AiReviewSkeleton';

export interface AiReviewSummaryProps {
  aiSummary?: AiReviewSummaryType;
  hideContent?: boolean;
  reviewsCount: number;
  className?: string;
}

export const AiReviewSummary = (props: AiReviewSummaryProps) => {
  const { t } = useSharedComponentsContext();
  const { showIntercom } = useIntercom();
  const { className, aiSummary, reviewsCount, hideContent = false } = props;

  const hasAiSummary = aiSummary != null && aiSummary.keyPoints != null && aiSummary.themes != null;

  return (
    <section
      data-testid="ai-review-summary"
      className={twMerge(
        'flex flex-col gap-4 rounded-xl border border-secondary-200 bg-white p-6 shadow-xs',
        className,
      )}>
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="mr-auto text-base font-semibold text-secondary-950">
          {t('base:aiReviewSummary.reviewSummary')}
        </h3>
        {hideContent && (
          <Tooltip content={t('base:aiReviewSummary.lockedTooltipDescription')} placement="top">
            <Chip
              size="xs"
              variant="flat"
              color="secondary"
              startContent={<Lock />}
              data-testid="ai-review-summary-locked-tag">
              {t('base:aiReviewSummary.locked')}
            </Chip>
          </Tooltip>
        )}
        {hideContent && (
          <Button
            size="xs"
            variant="light"
            color="primary"
            onPress={showIntercom}
            data-testid="ai-review-summary-tooltip-cta">
            {t('base:aiReviewSummary.lockedTooltipCta')}
          </Button>
        )}
        {hasAiSummary && !hideContent && (
          <Tooltip content={t('base:aiReviewSummary.aiGeneratedTooltip')} placement="top">
            <Chip
              size="xs"
              variant="bordered"
              color="secondary"
              startContent={<Sparkles />}
              data-testid="ai-review-summary-ai-generated-tag">
              {t('base:aiReviewSummary.aiGeneratedTag')}
            </Chip>
          </Tooltip>
        )}
        {hasAiSummary && !hideContent && (
          <Button
            size="xs"
            variant="light"
            color="secondary"
            onPress={showIntercom}
            data-testid="ai-generated-tooltip-cta">
            {t('base:aiReviewSummary.shareFeedback')}
          </Button>
        )}
      </header>
      {hasAiSummary && hideContent && <AiReviewSkeleton />}
      {hasAiSummary && !hideContent && (
        <AiReviewContent summary={aiSummary as DeepNonNullable<AiReviewSummaryType, 2>} />
      )}
      {!hasAiSummary && (
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-secondary-500">{t('base:aiReviewSummary.noSummaryAvailable')}</p>
          {reviewsCount < 3 && (
            <p className="text-secondary-500">{t('base:aiReviewSummary.noSummaryAvailableDescription')}</p>
          )}
        </div>
      )}
    </section>
  );
};
